import { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { generatePDF, generateImage } from '../utils/pdfGenerator';
import SecurityAssessmentReport from './SecurityAssessmentReport';
import type { SecurityAssessmentResponse, DocumentAnalysisResponse } from '../types';

interface GenerateSecurityReportDialogProps {
  open: boolean;
  onClose: () => void;
  currentAssessment: SecurityAssessmentResponse | null;
  csvAnalysis?: DocumentAnalysisResponse | null;
}

export default function GenerateSecurityReportDialog({
  open,
  onClose,
  currentAssessment,
  csvAnalysis,
}: GenerateSecurityReportDialogProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleGeneratePDF = useCallback(async () => {
    if (!reportRef.current || !currentAssessment) return;

    setIsGenerating(true);
    try {
      const date = new Date().toISOString().split('T')[0];
      const filename = `Pelles_Security_Assessment_${currentAssessment.orgName.replace(/[^a-zA-Z0-9]/g, '_')}_${date}.pdf`;
      await generatePDF(reportRef.current, filename);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [currentAssessment]);

  const handleGenerateImage = useCallback(async () => {
    if (!reportRef.current || !currentAssessment) return;

    setIsGenerating(true);
    try {
      const date = new Date().toISOString().split('T')[0];
      const filename = `Pelles_Security_Assessment_${currentAssessment.orgName.replace(/[^a-zA-Z0-9]/g, '_')}_${date}.png`;
      await generateImage(reportRef.current, filename, 'png');
    } catch (err) {
      console.error('Failed to generate image:', err);
    } finally {
      setIsGenerating(false);
    }
  }, [currentAssessment]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Export Security Assessment Report</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3, mt: 1 }}>
          {/* Report Preview */}
          {currentAssessment && (
            <Box>
              <Typography variant="caption" sx={{ color: '#1e293b', mb: 1, display: 'block' }}>
                Preview (scaled 70%)
              </Typography>
              <Box
                sx={{
                  maxHeight: 600,
                  overflow: 'auto',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: '#f5f5f5',
                  p: 2,
                }}
              >
                <Box sx={{ transform: 'scale(0.7)', transformOrigin: 'top left', width: '142.86%' }}>
                  <SecurityAssessmentReport ref={reportRef} data={currentAssessment} csvAnalysis={csvAnalysis || undefined} />
                </Box>
              </Box>
            </Box>
          )}

          {/* No Data State */}
          {!currentAssessment && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <Typography sx={{ color: '#1e293b' }}>
                No assessment data available. Please generate an assessment first.
              </Typography>
            </Box>
          )}

          {/* Generating State */}
          {isGenerating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2, color: '#1e293b' }}>Generating file...</Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isGenerating}>
          Cancel
        </Button>
        <Button
          onClick={handleGenerateImage}
          startIcon={<ImageIcon />}
          disabled={!currentAssessment || isGenerating}
          variant="outlined"
        >
          Download PNG
        </Button>
        <Button
          onClick={handleGeneratePDF}
          startIcon={<PictureAsPdfIcon />}
          disabled={!currentAssessment || isGenerating}
          variant="contained"
        >
          Download PDF
        </Button>
      </DialogActions>
    </Dialog>
  );
}
